import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { AngaMark } from "@/components/brand/AngaMark";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { liveApi } from "@/lib/api/live";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/alert-ack")({
  validateSearch: (search: Record<string, unknown>): { t?: string } => {
    const t = typeof search["t"] === "string" ? search["t"] : undefined;
    return t ? { t } : {};
  },
  head: () => ({
    meta: [
      { title: "Acknowledge alert" },
      { name: "description", content: "Confirm you received a flood advisory." },
    ],
  }),
  component: AlertAckPage,
});

function AlertAckPage() {
  const { t: tokenParam } = Route.useSearch();
  const { platformName } = usePlatformConfig();
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const token = useMemo(() => (tokenParam || "").trim(), [tokenParam]);

  const infoQ = useQuery({
    queryKey: ["public", "alert-ack", token],
    queryFn: () => liveApi.alertAckGet(token),
    enabled: token.length >= 10,
    retry: false,
  });

  const post = useMutation({
    mutationFn: (body: {
      action: "ack" | "feedback";
      outcome?: "YES_FLOODING" | "NO_FLOODING" | "NOT_SURE";
    }) => liveApi.alertAckPost({ t: token, ...body }),
    onSuccess: (res) => {
      setError(null);
      setDone(res.action === "ack" ? "Acknowledged. Thank you." : "Feedback recorded. Thank you.");
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Could not submit");
    },
  });

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--primary-soft),_transparent_55%)]" />
      <div
        className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm md:p-8"
        data-testid="alert-ack-page"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#064E3B] text-white">
            <AngaMark className="h-6 w-6" title={platformName} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-none">{platformName}</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Alert acknowledgement
            </p>
          </div>
        </div>

        {!token ? (
          <p className="text-sm text-muted-foreground" data-testid="alert-ack-missing-token">
            Open the link from your SMS (missing token <code className="text-xs">t</code>).
          </p>
        ) : infoQ.isError ? (
          <p className="rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
            {infoQ.error instanceof ApiError ? infoQ.error.message : "Invalid or expired link"}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {infoQ.isLoading
                ? "Loading alert…"
                : `Advisory for ${infoQ.data?.county || "your area"} · ${infoQ.data?.severity || "alert"}`}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              This is an advisory acknowledgement only — follow official emergency instructions if
              issued.
            </p>

            {done ? (
              <p
                className="mt-4 rounded-md border border-success/25 bg-success-soft px-3 py-2 text-sm text-success"
                data-testid="alert-ack-done"
              >
                {done}
              </p>
            ) : (
              <div className="mt-5 space-y-3">
                <Button
                  className="w-full"
                  data-testid="alert-ack-submit"
                  disabled={post.isPending || !token}
                  onClick={() => post.mutate({ action: "ack" })}
                >
                  {post.isPending ? "Sending…" : "I received this alert"}
                </Button>
                <div className="grid gap-2 sm:grid-cols-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={post.isPending}
                    onClick={() => post.mutate({ action: "feedback", outcome: "YES_FLOODING" })}
                  >
                    Flooding
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={post.isPending}
                    onClick={() => post.mutate({ action: "feedback", outcome: "NO_FLOODING" })}
                  >
                    No flooding
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={post.isPending}
                    onClick={() => post.mutate({ action: "feedback", outcome: "NOT_SURE" })}
                  >
                    Not sure
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {error ? (
          <p className="mt-4 rounded-md border border-destructive/25 bg-severe-soft px-3 py-2 text-xs text-severe">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
